import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import CompetitionStatusBadge from '../components/CompetitionStatusBadge';
import ExerciseCard from '../components/ExerciseCard';
import MenuShell from '../components/MenuShell';
import { useAuth } from '../features/auth/AuthContext';
import { fetchCourseExercises } from '../features/courses/courseApi';
import type { ExerciseCourse } from '../features/courses/courseApi';
import './CoursePage.css';

function CoursePage() {
  const { t } = useTranslation();
  const { role } = useAuth();
  const { courseId = '' } = useParams();
  const [course, setCourse] = useState<ExerciseCourse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let ignore = false;

    async function loadCourse() {
      try {
        setLoading(true);
        setError(null);
        const result = await fetchCourseExercises(courseId);
        if (!ignore) {
          setCourse(result);
        }
      } catch (loadError) {
        if (!ignore) {
          setError(loadError instanceof Error ? loadError.message : t('coursePage.defaultError'));
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    void loadCourse();
    return () => {
      ignore = true;
    };
  }, [courseId, t]);

  if (loading) {
    return (
      <>
        <MenuShell courseId={courseId} />
        <main className="course-page">{t('coursePage.loading')}</main>
      </>
    );
  }

  if (error) {
    return (
      <>
        <MenuShell courseId={courseId} />
        <main className="course-page">
          <p role="alert">{error}</p>
        </main>
      </>
    );
  }

  if (!course) {
    return null;
  }

  return (
    <>
      <MenuShell courseId={courseId} />
      <main className="course-page">
        <header className="course-page__hero">
          <Link className="course-page__back" to="/">
            &larr; {t('coursePage.backHome')}
          </Link>
          <h1>{course.courseTitle}</h1>
          <div className="course-page__hero-row">
            {role !== 'INSTRUCTOR' && (
              <Link to={`/courses/${course.courseId}/progress`}>
                {t('coursePage.viewProgress')}
              </Link>
            )}
            {role === 'INSTRUCTOR' && (
              <Link to={`/courses/${course.courseId}/badge-rules`}>
                {t('coursePage.manageBadgeRules')}
              </Link>
            )}
            <CompetitionStatusBadge courseId={course.courseId} />
          </div>
        </header>

        <section aria-labelledby="course-exercises-heading">
          <h2 id="course-exercises-heading">{t('coursePage.exercisesHeading')}</h2>
          {course.exercises.length > 0 ? (
            <div className="course-page__exercises">
              {course.exercises.map((exercise) => (
                <ExerciseCard key={exercise.id} exercise={exercise} />
              ))}
            </div>
          ) : (
            <p>{t('coursePage.emptyExercises')}</p>
          )}
        </section>
      </main>
    </>
  );
}

export default CoursePage;
